import { useEffect, useMemo, useState } from "react";
import { NotificationData, services } from "./utils";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { readDataStorage } from "../../utils/storage/storage";
import { RichTextRenderStatic } from "./rich-text/RichTextRenderStatic";

// ----------------------------------------------------------------------

const REFRESH_NOTIFICATIONS_INTERVAL = 1000 * 60 * 2;

// ----------------------------------------------------------------------

export default function NotificationsPage() {
  const { CpfRepresentante } = readDataStorage("login");
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  // ----------------------------------------------------------------------

  const [isLoading, setIsLoading] = useState(false);

  const getData = async () => {
    setIsLoading(true);
    try {
      await services.listNotifications(CpfRepresentante).then(setNotifications);
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //

  const refreshNotifications = async () => {
    try {
      await services.listNotifications(CpfRepresentante).then(setNotifications);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    refreshNotifications();

    const interval = setInterval(
      refreshNotifications,
      REFRESH_NOTIFICATIONS_INTERVAL
    );
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const todayNotifications = useMemo(
    () =>
      notifications?.filter((item) =>
        dayjs(item.date).isSame(new Date(), "day")
      ) || [],
    [notifications]
  );

  const yesterdayNotifications = useMemo(
    () =>
      notifications?.filter((item) =>
        dayjs(item.date).isSame(dayjs(new Date()).subtract(1, "day"), "day")
      ) || [],
    [notifications]
  );

  const olderNotifications = useMemo(
    () =>
      notifications?.filter(
        (item) =>
          !todayNotifications.includes(item) &&
          !yesterdayNotifications.includes(item)
      ) || [],
    [notifications, todayNotifications, yesterdayNotifications]
  );

  //

  const onRead = async (item: NotificationData) => {
    try {
      await services.setNotificationRead(item.id);
    } catch (error) {
      console.error(error);
    }
  };

  // ----------------------------------------------------------------------

  return (
    <Stack sx={{ overflow: "hidden", height: "100vh", width: "100vw" }}>
      <Stack
        sx={{
          px: 3,
          height: 52,
          backgroundColor: "#6c5af4",
          position: "relative",
          alignItems: "start",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            pointerEvents: "none",
          }}
        >
          <Typography
            sx={{
              color: "white",
              fontSize: 20,
              fontFamily: "Kanit",
              fontWeight: 400,
            }}
          >
            Notificações
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: 600,
          marginX: "auto",
          p: 2,
          overflowY: "auto",
        }}
      >
        {!isLoading && !notifications.length && (
          <Stack alignItems="center" justifyContent="center">
            <Typography
              sx={{
                fontSize: 16,
                color: "#787878",
                textAlign: "center",
                padding: "2rem 0",
              }}
            >
              Você não tem nenhuma notificação
            </Typography>
          </Stack>
        )}

        {isLoading && !notifications.length && (
          <Stack gap={1}>
            <div>
              <Skeleton variant="text" height={40} width={80} />
              <Stack>
                <Skeleton
                  variant="rectangular"
                  height={100}
                  sx={{ borderRadius: 2 }}
                />
              </Stack>
            </div>

            <div>
              <Skeleton variant="text" height={40} width={80} />
              <Stack gap={1}>
                <Skeleton
                  variant="rectangular"
                  height={100}
                  sx={{ borderRadius: 2 }}
                />
                <Skeleton
                  variant="rectangular"
                  height={100}
                  sx={{ borderRadius: 2 }}
                />
              </Stack>
            </div>
          </Stack>
        )}

        {!!todayNotifications.length && (
          <NotificationGroup
            label="Hoje"
            notifications={todayNotifications}
            onRead={onRead}
            onRefresh={getData}
          />
        )}

        {!!yesterdayNotifications.length && (
          <NotificationGroup
            label="Ontem"
            notifications={yesterdayNotifications}
            onRead={onRead}
            onRefresh={getData}
          />
        )}

        {!!olderNotifications.length && (
          <NotificationGroup
            label="Mais antiga"
            notifications={olderNotifications}
            onRead={onRead}
            onRefresh={getData}
          />
        )}
      </Box>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function NotificationGroup({
  label,
  notifications,
  onRead,
  onRefresh,
}: {
  label: string;
  notifications: NotificationData[];
  onRead: (item: NotificationData) => void;
  onRefresh: VoidFunction;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        pb: 2,
        gap: 1,
      }}
    >
      <Typography sx={{ fontWeight: 700 }}>{label}</Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {notifications?.map((item, index) => (
          <NotificationItem
            key={index}
            item={item}
            onRead={onRead}
            onRefresh={onRefresh}
          />
        ))}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

type NotificationItemProps = {
  item: NotificationData;
  onRead: (item: NotificationData) => void;
  onRefresh: VoidFunction;
};

function NotificationItem({ item, onRead, onRefresh }: NotificationItemProps) {
  const [showModal, setShowModal] = useState(false);

  const Header = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 4,
      }}
    >
      <Typography sx={{ flex: 1, fontSize: 20, fontWeight: 700 }}>
        {item.title}
      </Typography>

      <Typography>{dayjs(item.date).format("DD/MM HH:mm")}</Typography>
    </Box>
  );

  const handleClose = () => {
    setShowModal(false);
    onRefresh();
  };

  const handleOpen = () => {
    setShowModal(true);
    onRead(item);
  };

  return (
    <div>
      <Box onClick={handleOpen} sx={{ cursor: "pointer" }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid rgb(224 224 224)",
            ...(!item.read && {
              border: "2px solid #6c5af4",
              backgroundColor: "rgb(241 241 255)",
            }),
          }}
        >
          {Header}

          <Box sx={{ pt: 1.5 }}>
            <RichTextRenderStatic
              value={item.content}
              style={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                lineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            />
          </Box>
        </Box>
      </Box>

      <Dialog open={showModal} onClose={handleClose}>
        <DialogContent sx={{ width: 500 }}>
          {Header}

          <Box sx={{ pt: 1.5 }}>
            <RichTextRenderStatic value={item.content} />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              alignSelf: "end",
              backgroundColor: "#6c5af4",
              "&:hover": { backgroundColor: "#6c5af4DD" },
              textTransform: "none",
              fontSize: 16,
              borderRadius: 2,
            }}
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
